import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {
  Editor,
  EditorState,
  ContentState,
  RichUtils,
  convertToRaw,
  convertFromHTML,
  convertFromRaw,
  getDefaultKeyBinding
} from 'draft-js';
import debounce from 'lodash/debounce';

import { makeStyles } from '@material-ui/core/styles';

import { withoutSsr } from '@hydra/common/components';
import { useI18n } from '@hydra/common/components/i18n';
import BlockStyleControls from './BlockStyleControls';
import InlineStyleControls from './InlineStyleControls';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: "'Georgia', serif",
    fontSize: theme.typography.fontSizeMedium,
    padding: theme.spacing(2)
  },
  controls: {
    fontFamily: "'Helvetica', sans-serif",
    fontSize: theme.typography.fontSizeMedium,
    marginBottom: theme.spacing(0.5),
    userSelect: 'none',
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between'
  },
  editor: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    cursor: 'text',
    fontSize: theme.typography.fontSizeRoot,
    marginTop: theme.spacing(1.25),
    '& .public-DraftEditor-content': {
      margin: '0 -15px -15px',
      padding: '15px',
      minHeight: '100px'
    },
    '& .public-DraftEditorPlaceholder-root': {
      margin: '0 -15px -15px',
      padding: '15px',
      position: 'absolute',
      zIndex: 0,
      color: theme.palette.grey[500]
    },
    '& .public-DraftEditor-content[contenteditable=false]': {
      color: theme.palette.grey[200]
    }
  },
  maxLength: {
    paddingTop: theme.spacing(2),
    textAlign: 'right',
    borderTop: `1px solid ${theme.palette.grey[300]}`
  },
  noError: {
    color: theme.palette.grey[400]
  },
  error: {
    color: theme.palette.error.medium
  }
}));

const DEBOUNCE_DELAY = 600; // wait 600ms until propagating the change

// Custom overrides for 'code' style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: "'Inconsolata', 'Menlo', 'Consolas', monospace",
    fontSize: 16,
    padding: 2
  }
};

const getEditorStateFromProps = ({ value, defaultContent, translate }) => {
  if (value && value.blocks) {
    return EditorState.createWithContent(convertFromRaw(value));
  }

  const translatedDefaultContent = typeof defaultContent === 'string' ? translate(defaultContent) : null;
  return translatedDefaultContent
    ? EditorState.createWithContent(ContentState.createFromBlockArray(convertFromHTML(translatedDefaultContent)))
    : EditorState.createEmpty();
};

export const RichEditor = props => {
  useEffect(() => {
    import('draft-js/dist/Draft.css');
  }, []);

  const classes = useStyles();
  const { translate } = useI18n();
  const {
    maxLength = 0,
    allowNewLines = true,
    displayBlockStyleControls = true,
    defaultContent,
    value,
    onChange,
    disabled,
    placeholder
  } = props;
  const [editorState, setEditorState] = useState(getEditorStateFromProps({ value, defaultContent, translate }));
  const [hasFocus, setHasFocus] = useState(false);
  const editorEl = useRef(null);

  const propagateChange = debounce(editorState => {
    if (hasFocus) {
      const serializedContent = convertToRaw(editorState.getCurrentContent());
      onChange(serializedContent);
    }
  }, DEBOUNCE_DELAY);

  const handleChange = (editorState) => {
    if (maxLength === 0 || editorState.getCurrentContent().getPlainText().length <= maxLength) {
      propagateChange(editorState);
      setEditorState(editorState);
    } else if (editorState.getCurrentContent().getPlainText().length > maxLength) {
      const trimmedText = editorState.getCurrentContent().getPlainText().substring(0, maxLength);
      const newEditorState = EditorState.createWithContent(
        ContentState.createFromBlockArray(convertFromHTML(trimmedText))
      );
      propagateChange(newEditorState);
      setEditorState(newEditorState);
    } else {
      return false;
    }
  };

  const onBeforeInput = (chars) => {
    if (maxLength === 0) {
      return false;
    }

    const totalLength = editorState.getCurrentContent().getPlainText().length + chars.length;
    return totalLength > maxLength;
  };

  const onKeyCommand = (command, editorState) => {
    if (!allowNewLines && command === 'split-block') {
      return true;
    }
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleChange(newState);
      return true;
    }
    return false;
  };

  const myKeyBindingFn = (e) => {
    if (e.keyCode === 9) {
      const maxDepth = 4;
      return handleChange(RichUtils.onTab(e, editorState, maxDepth));
    }
    return getDefaultKeyBinding(e);
  };

  const handleBlockType = (blockType) => {
    handleChange(
      RichUtils.toggleBlockType(
        editorState,
        blockType
      )
    );
  };

  const handleInlineStyle = (inlineStyle) => {
    handleChange(
      RichUtils.toggleInlineStyle(
        editorState,
        inlineStyle
      )
    );
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    propagateChange.flush();
    setHasFocus(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.controls}>
        <InlineStyleControls editorState={editorState} onToggle={handleInlineStyle} />
        {
          displayBlockStyleControls
            ? <BlockStyleControls editorState={editorState} onToggle={handleBlockType} />
            : null
        }
      </div>
      <div
        className={classes.editor}
        onClick={() => editorEl.current.props.onFocus()}
      >
        <Editor
          placeholder={placeholder || ''}
          customStyleMap={styleMap}
          editorState={editorState}
          handleKeyCommand={onKeyCommand}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          handleBeforeInput={onBeforeInput}
          keyBindingFn={myKeyBindingFn}
          readOnly={disabled}
          ref={editorEl}
          spellCheck
        />
      </div>
      {
        maxLength
          ? (
            <div
              data-testid='maxlength-wrapper'
              className={classnames(
                classes.maxLength,
                editorState.getCurrentContent().getPlainText().length === maxLength ? classes.error : classes.noError
              )}
            >
              {editorState.getCurrentContent().getPlainText().length} / {maxLength}
            </div>
            )
          : ''
      }
    </div>
  );
};

RichEditor.propTypes = {
  disabled: PropTypes.bool,
  meta: PropTypes.shape({
    initial: PropTypes.object
  }),
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      blocks: PropTypes.array
    }),
    PropTypes.string
  ])
};

export default withoutSsr(RichEditor);
